import { getReadDepartmentIds } from 'server/helpers/permissions/departmentPermissions';
import { DepartmentPermissions } from 'server/shared/models/dispoPermissions';
import { Organisation } from 'server/shared/models/organisation';
import { PoolerUser } from 'server/shared/models/user';

export function filterOrganisationsByPermissions(
  organisations: Organisation[],
  departmentPermissions: DepartmentPermissions,
): Organisation[] {
  if (departmentPermissions.readAll) {
    return organisations;
  }
  const readDepartmentIds = getReadDepartmentIds(departmentPermissions);
  return organisations
    .filter((organisation) =>
      organisation.departments.some((department) =>
        readDepartmentIds.includes(department.id),
      ),
    )
    .map((organisation) => {
      organisation.departments = organisation.departments.filter((department) =>
        readDepartmentIds.includes(department.id),
      );
      return organisation;
    });
}

export function filterOrganisationsByDepartmentIds(
  organisations: Organisation[],
  departmentIds: string[],
): Organisation[] {
  return organisations
    .map((organisation) => {
      const departments = organisation.departments.filter((department) =>
        departmentIds.includes(department.id),
      );
      return {
        ...organisation,
        departments,
      };
    })
    .filter((organisation) => organisation.departments.length > 0);
}

export function filterOrganisationsByPoolerPermission(
  user: PoolerUser,
  organisations: Organisation[],
): Organisation[] {
  const validOrganisationMap = new Map(
    Array.from(
      new Set(
        Object.keys(user.customerInformation ?? {}).flatMap((customerId) => {
          const customerInfo = user.customerInformation?.[customerId];
          if (!customerInfo) return [];

          const userDepartmentIds = new Set(customerInfo.departmentIds ?? []);
          const userTags = new Set(
            (customerInfo.tags ?? []).map((tag) => tag.id),
          );

          return organisations
            .filter((org) => {
              const hasCustomerInfo =
                org.customerInformation &&
                Object.keys(org.customerInformation).length > 0;
              if (!hasCustomerInfo) return true;

              const hasDepartments = org.departments.some((dept) =>
                userDepartmentIds.has(dept.id),
              );
              if (!hasDepartments) return false;

              const orgTags = org.customerInformation?.[customerId]?.tags ?? [];
              if (orgTags.length === 0) return true;
              if (userTags.size === 0) return false;

              return orgTags.some((tag) => userTags.has(tag.id));
            })
            .map((org) => org._id);
        }),
      ),
    ).map((id) => [id, true]),
  );

  return organisations.filter((org) => validOrganisationMap.has(org._id));
}
